import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowRight, Store, Sparkles, Activity } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { Card, CardHeader, CardTitle, CardDescription, CardBody, Badge, Avatar } from '@/components/ui';

/*
  Home — приветственный dashboard для авторизованного юзера.
  На данном этапе — обзор основных разделов + текущее состояние платформы.

  Sprint 1.15: «Состояние платформы» теперь derived from /api/health
  (backend агрегирует Postgres, NATS, Auth, AI + динамику адаптеров).
  Хардкод убран. Polling каждые 15s — не агрессивно, но достаточно чтобы
  изменения видеть без F5.
*/

interface HealthResponse {
  generated_at: string;
  components: Record<string, { status: string; label: string }>;
  adapters: Array<{
    name: string;
    display_name: string;
    status: string;
    label: string;
    message?: string;
  }>;
}

// Маппинг backend-status → Badge variant. Хранится тут (UI knows wording),
// label приходит уже готовый из backend.
type StatusTone = 'success' | 'warn' | 'urgent' | 'info' | 'neutral';

function toneForStatus(status: string): StatusTone {
  switch (status) {
    case 'healthy':
      return 'success';
    case 'degraded':
      return 'warn';
    case 'unhealthy':
      return 'urgent';
    case 'disabled':
    case 'unknown':
    default:
      return 'neutral';
  }
}

export function Home() {
  const { state } = useAuth();
  const user = state.status === 'authenticated' ? state.user : null;
  const firstName = user?.display_name?.split(' ')[0] ?? 'друг';
  const health = useSystemHealth();

  return (
    <div className="mx-auto max-w-5xl px-8 py-10">
      {/* Hero */}
      <header className="mb-10">
        <div className="flex items-center gap-3">
          <Avatar src={user?.avatar_url} name={user?.display_name} size="xl" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Привет, {firstName} 👋</h1>
            <p className="mt-1 text-sm text-fg-muted">Что построим сегодня?</p>
          </div>
        </div>
      </header>

      {/* Quick-access cards */}
      <section aria-labelledby="quick-access" className="mb-10">
        <h2 id="quick-access" className="mb-3 text-xs font-semibold tracking-wider text-fg-subtle uppercase">
          Быстрый доступ
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <QuickCard
            to="/marketplace"
            icon={<Store />}
            title="Marketplace"
            description="Готовые модули от коллег — установи в свой workspace"
            accent="neutral"
          />
          <QuickCard
            to="/portfolio/me"
            icon={<Sparkles />}
            title="Создать модуль"
            description="Своя страница, AI-инструмент или интеграция"
            accent="ai"
          />
        </div>
      </section>

      {/* Platform status — derived from /api/health */}
      <section aria-labelledby="status">
        <h2 id="status" className="mb-3 text-xs font-semibold tracking-wider text-fg-subtle uppercase">
          Состояние платформы
        </h2>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Activity
                  size={15}
                  className={
                    health.overall === 'healthy'
                      ? 'text-service-enabled-accent'
                      : health.overall === 'degraded'
                        ? 'text-live-warning-accent'
                        : 'text-fg-subtle'
                  }
                />
                {health.overall === 'healthy'
                  ? 'Системы работают'
                  : health.overall === 'degraded'
                    ? 'Системы с замечаниями'
                    : health.overall === 'loading'
                      ? 'Проверяем статус…'
                      : 'Статус недоступен'}
              </CardTitle>
              <Badge variant={overallBadgeVariant(health.overall)} dot>
                {overallBadgeLabel(health.overall)}
              </Badge>
            </div>
            <CardDescription>
              Обновляется каждые 15 секунд. Источник — `/api/health` агрегирующий все runtime-компоненты.
            </CardDescription>
          </CardHeader>
          <CardBody>
            {health.rows.length === 0 ? (
              <p className="text-sm text-fg-subtle">
                {health.overall === 'loading' ? 'Загрузка…' : 'Нет данных.'}
              </p>
            ) : (
              <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-3">
                {health.rows.map((row) => (
                  <StatusRow key={row.key} label={row.label} value={row.value} tone={row.tone} />
                ))}
              </dl>
            )}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}

// ---- /api/health polling hook ----

interface StatusRowData {
  key: string;
  label: string;
  value: string;
  tone: StatusTone;
}

interface HealthVM {
  overall: 'healthy' | 'degraded' | 'unhealthy' | 'loading' | 'error';
  rows: StatusRowData[];
}

function useSystemHealth(): HealthVM {
  const [vm, setVm] = useState<HealthVM>({ overall: 'loading', rows: [] });

  useEffect(() => {
    let cancelled = false;

    async function fetchHealth() {
      try {
        const resp = await fetch('/api/health', { credentials: 'include' });
        if (!resp.ok) throw new Error(`/api/health ${resp.status}`);
        const data: HealthResponse = await resp.json();
        if (cancelled) return;
        setVm(buildVM(data));
      } catch (err) {
        if (cancelled) return;
        console.warn('health fetch failed', err);
        setVm((prev) => (prev.rows.length > 0 ? prev : { overall: 'error', rows: [] }));
      }
    }

    fetchHealth();
    const id = window.setInterval(fetchHealth, 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return vm;
}

/*
  buildVM — превращает /api/health payload в плоский список строк для UI.
  Порядок: Auth, адаптеры, Event bus, Storage, AI
  (и далее любые дополнительные адаптеры). Совпадает с прежним hardcoded
  layout'ом так что визуально checkpoint не меняется кроме values.
*/
function buildVM(data: HealthResponse): HealthVM {
  const rows: StatusRowData[] = [];

  // Группа 1 — статичные компоненты в фиксированном порядке
  const labelMap: Record<string, string> = {
    auth: 'Auth',
    storage: 'Storage',
    event_bus: 'Event bus',
    ai_agent: 'AI-агент',
  };
  const order = ['auth', 'event_bus', 'storage', 'ai_agent'];

  // Адаптеры размещаем между Auth и Event Bus — там где раньше был
  // "Slack-адаптер" статичный.
  const adapterRows: StatusRowData[] = data.adapters.map((a) => ({
    key: `adapter:${a.name}`,
    label: `${a.display_name}-адаптер`,
    value: a.label,
    tone: toneForStatus(a.status),
  }));

  // Build final order: auth → adapters → event_bus → storage → ai_agent
  for (const k of ['auth']) {
    const comp = data.components[k];
    if (comp) rows.push({ key: k, label: labelMap[k], value: comp.label, tone: toneForStatus(comp.status) });
  }
  rows.push(...adapterRows);
  for (const k of order.slice(1)) {
    const comp = data.components[k];
    if (comp) rows.push({ key: k, label: labelMap[k], value: comp.label, tone: toneForStatus(comp.status) });
  }

  // Overall: worst-case wins. unhealthy > degraded > healthy. disabled/unknown
  // НЕ считаем плохими (это feature flags, не сбои).
  let overall: HealthVM['overall'] = 'healthy';
  const allStatuses = [
    ...Object.values(data.components).map((c) => c.status),
    ...data.adapters.map((a) => a.status),
  ];
  if (allStatuses.some((s) => s === 'unhealthy')) overall = 'unhealthy';
  else if (allStatuses.some((s) => s === 'degraded')) overall = 'degraded';

  return { overall, rows };
}

function overallBadgeVariant(o: HealthVM['overall']): StatusTone {
  switch (o) {
    case 'healthy':
      return 'success';
    case 'degraded':
      return 'warn';
    case 'unhealthy':
      return 'urgent';
    default:
      return 'neutral';
  }
}

function overallBadgeLabel(o: HealthVM['overall']): string {
  switch (o) {
    case 'healthy':
      return 'Всё ок';
    case 'degraded':
      return 'Деградация';
    case 'unhealthy':
      return 'Авария';
    case 'loading':
      return 'Загрузка';
    case 'error':
      return 'Нет связи';
  }
}

interface QuickCardProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  accent: 'coral' | 'neutral' | 'ai';
}

function QuickCard({ to, icon, title, description, accent }: QuickCardProps) {
  const accentClasses: Record<QuickCardProps['accent'], string> = {
    coral: 'bg-brand-accent/10 text-brand-accent',
    neutral: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    ai: 'bg-ai-accent/10 text-ai-accent',
  };
  /*
    Sequential animation (motion principles правило 3):
    - Card на hover: lift -3px + scale + shadow (Card.interactive это делает)
    - Arrow на hover внутри: с delay 150ms запускается keyframe arrow-flick
      (anticipation: pull-back -3px → forward +8px). Глаз ловит sequence
      «карточка отреагировала → потом стрелка стрельнула вперёд».
  */
  return (
    <Link to={to} viewTransition className="group/quick block outline-none">
      <Card interactive className="h-full">
        <CardHeader>
          <div className="flex items-start justify-between">
            <span
              className={`grid size-10 place-items-center rounded-md ${accentClasses[accent]} [&_svg]:size-5`}
            >
              {icon}
            </span>
            <ArrowRight
              size={16}
              className="text-fg-subtle transition-colors duration-normal group-hover/quick:animate-quick-arrow group-hover/quick:text-brand-accent"
            />
          </div>
          <CardTitle className="mt-3">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
      </Card>
    </Link>
  );
}

function StatusRow({ label, value, tone }: { label: string; value: string; tone: StatusTone }) {
  return (
    <div>
      <dt className="text-2xs tracking-wider text-fg-subtle uppercase">{label}</dt>
      <dd className="mt-0.5">
        <Badge variant={tone} dot>
          {value}
        </Badge>
      </dd>
    </div>
  );
}
